
import { Vue, Component, Prop } from "vue-property-decorator";

import UPDATE_CUSTOM_REQUEST_WITH_SKILLS from "@/apollo/requests/UpdateCustomRequestWithSkills.gql";
import CREATE_CUSTOM_REQUEST from "@/apollo/requests/CreateCustomRequest.gql";
import CREATE_CUSTOM_REQUEST_SKILLS from "@/apollo/requests/CreateCustomRequestSkills.gql";

import CustomRequestStatusMultiselect from "@/components/autocompleteMultiselects/CustomRequestStatusMultiselectNew.vue";
import FormTextArea from "@/components/form/FormTextarea.vue";
import FormGroup from "@/components/form/FormGroupNew.vue";
import FormInputGroup from "@/components/form/FormInputGroup.vue";
import UIButton from "@/components/button/ui-button.vue";
import ModalFullScreen from "@/components/modal/ModalFullScreenNew.vue";
import SkillsMultiselect from "@/components/autocompleteMultiselects/SkillsMultiselectNew.vue";
import FormDatepicker from "@/components/form/FormDatepicker.vue";
import TagMultiselect from "@/components/autocompleteMultiselects/TagMultiselectRequest.vue";

import Message from "@/data/notificationsMessages";
import { NOTIFICATION } from "@/entities/Events";
import { Custom_Request } from "@/generated/graphql";
import { getSelectedStatusDropdownOption } from "@/helpers/requests/getStatus";
import { customRequestModalSkillsCount } from "@/data/multiselectChipsLimit";
import CustomRequestFormData from "@/entities/customRequests/CustomRequestFormData";
import {
  customRequestCreatedMessage,
  customRequestUpdatedMessage,
} from "@/data/successMessages";

@Component({
  components: {
    FormTextArea,
    UIButton,
    ModalFullScreen,
    FormGroup,
    FormInputGroup,
    SkillsMultiselect,
    CustomRequestStatusMultiselect,
    FormDatepicker,
    TagMultiselect,
  },
})
export default class CustomRequestModal extends Vue {
  @Prop({ type: String, required: true }) readonly currentUserId!: string;
  @Prop({ type: Boolean, default: false }) readonly isAdmin!: boolean;
  @Prop({ type: String, required: true }) readonly companyId!: string;
  @Prop({ type: Object, default: () => undefined })
  readonly customRequest?: Custom_Request;
  @Prop({ type: Array, default: () => [] }) readonly skills!: number[];
  @Prop({ type: Boolean, default: false }) readonly outerHandler!: boolean;
  @Prop({ type: Boolean, default: false }) readonly outerProcessing!: boolean;

  request: CustomRequestFormData = {
    id: undefined,
    description: "",
    jobTitle: "",
    skills: [],
    createdBy: undefined,
    status: undefined,
    //@ts-ignore
    number_of_contractors: "",
    min_budget: "",
    max_budget: "",
    from_date: "",
    to_date: "",
    tag_id: null,
  };

  innerProcessing: boolean = false;
  skillsMultiselectLimit: number = customRequestModalSkillsCount;
  selectedTagId: string | null = null;

  mounted() {
    this.assignCustomRequestFromProp();
    this.assignCustomeRequestSkillsFromProp();
  }

  // setTagId(tagId: string): void {

  //     this.request.tag_id  = tagId.length === 0 ? null : tagId;
  //   }

  setTagId(tagId: string): void {
    this.selectedTagId = tagId.length === 0 ? null : tagId;
  }
  get processing(): boolean {
    return this.innerProcessing || this.outerProcessing;
  }

  get modalTitle(): string {
    return this.customRequest?.id
      ? "Update Talent Hunt request"
      : "Talent Hunt request";
  }

  get showStatusDropdown(): boolean {
    return Boolean(this.customRequest) && this.isAdmin;
  }

  assignCustomRequestFromProp(): void {
    if (this.customRequest) {
      const status = getSelectedStatusDropdownOption(this.customRequest.status);
      const min_budget = this.customRequest.min_budget.toString();
      const max_budget = this.customRequest.max_budget.toString();
      const number_of_contractors = this.customRequest.number_of_contractors;
      const from_date = this.customRequest.from_date;
      const to_date = this.customRequest.to_date;
      const tag_id = this.customRequest.tag_id;

      this.request = {
        ...this.request,
        id: this.customRequest.id,
        description: this.customRequest.description || "",
        jobTitle: this.customRequest.job_title,
        createdBy: this.customRequest.created_by_id,
        status: status,
        min_budget: min_budget,
        max_budget: max_budget,
        number_of_contractors,
        from_date,
        to_date,
        tag_id,
      };
    }
  }

  assignCustomeRequestSkillsFromProp(): void {
    if (this.skills.length > 0) {
      this.request = {
        ...this.request,
        skills: this.skills,
      };
    }
  }

  async onSubmit(): Promise<void> {
    if (this.customRequest?.id) {
      if (this.outerHandler) {
        this.$emit("update", this.request);
        return;
      }

      return await this.handleUpdate();
    }

    if (this.outerHandler) {
      this.$emit("create", this.request);
      return;
    }

    return await this.handleCreate();
  }

  handleClose(): void {
    this.$emit("close");
  }

  async handleCreate(): Promise<void> {
    //@ts-ignore
    const {
      jobTitle,
      description,
      number_of_contractors,
      min_budget,
      max_budget,
      from_date,
      to_date,
      tag_id,
      status,
    } = this.request;

    const request = {
      description,
      job_title: jobTitle,
      created_by_id: this.currentUserId,
      company_id: this.companyId,
      status,
      number_of_contractors,
      min_budget: min_budget.toString(),
      max_budget: max_budget.toString(),
      from_date,
      to_date,
      tag_id,
    };
    try {
      this.innerProcessing = true;
      const newRequest = await this.$apollo.mutate({
        mutation: CREATE_CUSTOM_REQUEST,
        variables: { object: request },
      });
      const newRequestId = newRequest.data.insert_custom_request_one.id;
      const skills = this.request.skills;

      if (skills.length) {
        const requestSkillsObjects = skills.map((skillId) => ({
          custom_request_id: newRequestId,
          skill_id: skillId,
        }));

        await this.$apollo.mutate({
          mutation: CREATE_CUSTOM_REQUEST_SKILLS,
          variables: { objects: requestSkillsObjects },
        });
      }
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage(customRequestCreatedMessage)
      );
      this.$emit("refetch");
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    } finally {
      this.innerProcessing = false;
      this.handleClose();
    }
  }

  async handleUpdate(): Promise<void> {
    const {
      jobTitle,
      description,
      status,
      skills,
      id,
      from_date,
      to_date,
      min_budget,
      max_budget,
      number_of_contractors,
      tag_id,
    } = this.request;

    const statusForDB = status![0];
    const updatedSkills = skills.map((skillId) => ({
      custom_request_id: id,
      skill_id: skillId,
    }));

    const updatedRequest = {
      description,
      job_title: jobTitle,
      status: statusForDB,
      updated_by_id: this.currentUserId,
      to_date,
      from_date,
      min_budget: min_budget.toString(),
      max_budget: max_budget.toString(),
      number_of_contractors,
      tag_id,
    };

    try {
      this.innerProcessing = true;
      await this.$apollo.mutate({
        mutation: UPDATE_CUSTOM_REQUEST_WITH_SKILLS,
        variables: {
          set: updatedRequest,
          id: this.customRequest.id,
          skillsSet: updatedSkills,
        },
      });
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage(customRequestUpdatedMessage)
      );
      this.$emit("refetch");
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    } finally {
      this.innerProcessing = false;
      this.handleClose();
    }
  }
}
